import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LogoutOutlined, ShoppingBagOutlined } from '@mui/icons-material';

import { Logo, StyledAppBar } from '../../styledComponent';
import { SignOutButton, StyledToolbar, ToolbarActions } from './header.styles';
import { BagButton, BagCount } from '../cart/cart.styled';
import CartDrawer from '../cart/cart';
import { RootState } from '../../../redux';
import { openCart } from '../../../redux/cart';
import { useAuth } from '../../../context/authContext';

/**
 * Public header: brand and bag.
 *
 * There is deliberately no sign-in control here. Signing in is for staff, and
 * the page is reached by typing /signin directly rather than by advertising it
 * to shoppers. Customers never need an account — the 10% email discount is
 * collected in the checkout form instead.
 *
 * Signing *out* is different: once an admin is in, the control belongs in the
 * navbar so it is reachable from any page. Shoppers never see it.
 */
const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAdmin, logout } = useAuth();

  const lines = useSelector((state: RootState) => state.cart.lines);
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

  const handleSignOut = () => {
    logout();
    navigate('/signin', { replace: true });
  };

  return (
    <>
      <StyledAppBar position="sticky" elevation={0}>
        <StyledToolbar>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Logo>
              <img
                src="/img/logo-bold.png"
                alt="Klarity"
                style={{ height: '35px', width: '160px' }}
              />
            </Logo>
          </Link>

          <ToolbarActions>
            {isAdmin && (
              <SignOutButton type="button" onClick={handleSignOut}>
                <LogoutOutlined sx={{ fontSize: 16 }} />
                Sign out
              </SignOutButton>
            )}

            <BagButton
              type="button"
              onClick={() => dispatch(openCart())}
              aria-label={itemCount > 0 ? `Open bag, ${itemCount} items` : 'Open bag'}
            >
              <ShoppingBagOutlined sx={{ fontSize: 17 }} />
              Bag
              {itemCount > 0 && <BagCount>{itemCount}</BagCount>}
            </BagButton>
          </ToolbarActions>
        </StyledToolbar>
      </StyledAppBar>

      <CartDrawer />
    </>
  );
};

export default Header;
